import { CardInfoType, CardStatusType, CardType } from '@finxone-platform/shared/sys-config-types';
import { isIOS } from './device-detection.utils';
import { capitalize } from './utils';

/**
 * Common function to pass `loadEvent` from various point in the app
 * @param event
 */
export function logLoadEventToFlutter(event: string) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const flutterWebView = (window as any).flutter_inappwebview;
  if (flutterWebView) {
    flutterWebView.callHandler('loadEvent', [event]);
  }
}

/**
 * Retrieves the wallet token list from either Flutter WebView or localStorage fallback.
 * Used by both Apple Pay and Google Pay components to check if cards are already
 * added to the respective wallet services.
 *
 * Google and Apple originally had different handlers but if you check the flutter code
 * you will see they both return a `tokenList` object the same way from the same place:
 * final tokenList = await TokenStorageService().getTokenList();
 *
 * @returns Promise resolving to an object containing an array of wallet tokens
 * @throws Never throws - returns empty tokenList array on any error
 */
export async function getWalletTokenList(): Promise<{ tokenList: any[] }> {
  const flutterWebView = (window as any).flutter_inappwebview;
  try {
    if (flutterWebView) {
      const result = await flutterWebView.callHandler('getTokenList', []);
      // Ensure we always return the expected structure
      if (result && typeof result === 'object' && Array.isArray(result.tokenList)) {
        return result;
      }
      return { tokenList: [] };
    } else {
      // Uses local storage if flutterWebView is not available
      const tokenList = localStorage.getItem('tokenList');
      if (tokenList) {
        try {
          const parsed = JSON.parse(tokenList) || [];
          return { tokenList: Array.isArray(parsed) ? parsed : [] };
        } catch (error) {
          console.error('Error parsing token list from local storage:', error);
          return { tokenList: [] };
        }
      }
    }
    return { tokenList: [] };
  } catch (e) {
    console.error('Error fetching wallet token list:', e);
    return { tokenList: [] };
  }
}

/**
 * Passes active cards to native storage for Apple Wallet Extension
 * @param cardList
 */
export function passActiveCardsToNativeStorage(cardList: CardInfoType[]): void {
  if (!isIOS() || !cardList.length) return;

  const walletReadyCards: Record<string, ProvisioningCredential> = cardList
    .filter((card) => card.status.toLowerCase() === CardStatusType.ACTIVE)
    .reduce((acc, card) => {
      acc[card.id] = mapToProvisioningCredential(card);
      return acc;
    }, {} as Record<string, ProvisioningCredential>);

  const flutterWebView = (window as any).flutter_inappwebview;
  if (flutterWebView) {
    flutterWebView.callHandler('setCardListHandler', [walletReadyCards]);
  }
}

/**
 * Maps card details to ProvisioningCredential schema needed by Apple Wallet
 */
function mapToProvisioningCredential(card: CardInfoType): ProvisioningCredential {
  return {
    isAvailableForProvisioning: true, // already checked 'active'
    primaryAccountIdentifier: card.id,
    primaryAccountSuffix: card.maskedPan.replace(/[^0-9]/g, '').slice(-4),
    localizedDescription: `${card.currency} ${capitalize(card.type)} Card`,
    cardholderName: card.cardholderName || '',
    expiration: card.expiry,
    assetName: determineAssetName(card.type),
    label: card.name || '',
  };
}

/**
 * Helper function to determine the appropriate card art asset
 */
function determineAssetName(cardType: string): string {
  switch (cardType.toLowerCase()) {
    case CardType.VIRTUAL:
      return 'virtual_card';
    case CardType.PHYSICAL:
      return 'physical_card';
    default:
      return 'generic_card';
  }
}

/**
 * Waits for flutter_inappwebview to be populated on the window object.
 * Resolves with the flutterWebView object or null if not found within timeout.
 */
export function waitForFlutterWebView(timeout = 3000, interval = 100): Promise<any> {
  return new Promise((resolve) => {
    const start = Date.now();
    const check = () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const flutterWebView = (window as any).flutter_inappwebview;
      if (flutterWebView && typeof flutterWebView.callHandler === 'function') {
        resolve(flutterWebView);
      } else if (Date.now() - start >= timeout) {
        resolve(null);
      } else {
        setTimeout(check, interval);
      }
    };
    check();
  });
}

/**
 * Required fields for ProvisioningCredential structure for Apple Wallet
 */
interface ProvisioningCredential {
  isAvailableForProvisioning: boolean;
  primaryAccountIdentifier: string;
  primaryAccountSuffix: string;
  localizedDescription: string;
  cardholderName: string;
  expiration: string;
  assetName: string;
  label: string;
}
